import { useCallback, useEffect, useState } from "react";
import postsService from "../services/postsService";
import type { AudioConfig } from "../components/modals/MultiPlatformPublishModal";
import {
  showConfirm,
  showError,
  showInfo,
  showProgress,
  showSuccess,
  showToastError,
  showToastSuccess,
  showWarning,
} from "../shared/alertService";
import apiService, { API_ENDPOINTS } from "../shared/apiService";
import { getPlatformName } from "../utils/platformIcons";
import type { UsePostsModalsReturn } from "./usePostsModals";

interface UsePostsPublishingProps {
  loadPosts: (forceRefresh?: boolean) => Promise<void>;
  approvedPosts: any[];
  modals: UsePostsModalsReturn;
  user?: any;
  getMinDate: () => string;
  setShowScheduleModal: (id: number | null) => void;
  setScheduleDate: (date: string) => void;
}

export interface UsePostsPublishingReturn {
  showPublishModal: number | null;
  setShowPublishModal: (id: number | null) => void;
  linkedinConfig: {
    can_publish_personal: boolean;
    can_publish_organization: boolean;
    organization_name?: string;
    default_type: string;
  } | null;
  publishOptions?: Array<{ value: string; label: string }>;
  selectedOption: string;
  setSelectedOption: (option: string) => void;
  showPublishOptions: (postId: number) => void;
  publishPost: (postId: number, linkedinType?: string) => Promise<void>;
  handlePublishConfirm: (publishType?: string) => Promise<void>;
  handleMultiPlatformPublish: (platforms: string[]) => Promise<void>;
  handleRepublishLinkedIn: (usePersonalNetwork: boolean) => Promise<void>;
  handleRepublishMultiPlatform: (platforms: string[]) => Promise<void>;
  handleReplyToComment: (
    platform: "instagram" | "facebook" | "linkedin",
    commentId: string,
    commentText: string,
    postUrn?: string,
  ) => Promise<any>;
  openMultiPlatformModal: (post: any) => void;
  openUnifiedRepublishModal: (post: any) => void;
}

export function usePostsPublishing({
  loadPosts,
  approvedPosts,
  modals,
  user,
  getMinDate,
  setShowScheduleModal,
  setScheduleDate,
}: UsePostsPublishingProps): UsePostsPublishingReturn {
  const [showPublishModal, setShowPublishModal] = useState<number | null>(null);
  const [linkedinConfig, setLinkedinConfig] = useState<{
    can_publish_personal: boolean;
    can_publish_organization: boolean;
    organization_name?: string;
    default_type: string;
  } | null>(null);
  const [publishOptions] = useState([
    { value: "personal", label: "Perfil Pessoal" },
    { value: "organization", label: "Organização" },
  ]);
  const [selectedOption, setSelectedOption] = useState("personal");

  useEffect(() => {
    const fetchLinkedinConfig = async () => {
      try {
        const config = await apiService.get(
          API_ENDPOINTS.SETTINGS.LINKEDIN_CONFIG,
        );
        setLinkedinConfig(config);
      } catch (error) {
        console.error("❌ Erro ao buscar configuração do LinkedIn:", error);
      }
    };
    fetchLinkedinConfig();
  }, []);

  const showPublishOptions = useCallback(
    (postId: number) => {
      const post = approvedPosts.find((p) => p.id === postId);
      const isLinkedin = post?.platform === "linkedin";

      if (isLinkedin) {
        if (
          linkedinConfig &&
          linkedinConfig.can_publish_personal &&
          !linkedinConfig.can_publish_organization
        ) {
          publishPost(postId, "personal");
        } else if (
          linkedinConfig &&
          !linkedinConfig.can_publish_personal &&
          linkedinConfig.can_publish_organization
        ) {
          publishPost(postId, "organization");
        } else {
          setShowPublishModal(postId);
        }
      } else {
        // Not LinkedIn, just show a generic modal or publish directly
        // We can reuse the modal but without publishOptions, or just call publishPost
        setShowPublishModal(postId);
      }
    },
    [linkedinConfig, approvedPosts],
  );

  const publishPost = useCallback(
    async (postId: number, linkedinType?: string) => {
      const post = approvedPosts.find((p) => p.id === postId);
      const isLinkedin = post?.platform === "linkedin";

      let target = "";
      if (isLinkedin) {
        const organizationName = user?.company
          ? `página da empresa (${user.company})`
          : "página da empresa";
        target =
          linkedinType === "organization" ? organizationName : "perfil pessoal";
      } else if (post?.platform) {
        target = `Plataforma: ${getPlatformName(post.platform)}`;
      } else {
        target = "Plataforma selecionada";
      }

      if (
        post &&
        (post as any).status === "scheduled" &&
        (post as any).scheduled_date &&
        (post as any).scheduled_time
      ) {
        try {
          const scheduledDateTime = new Date(
            `${(post as any).scheduled_date}T${(post as any).scheduled_time}:00`,
          );
          const now = new Date();

          if (scheduledDateTime < now) {
            const rescheduleChoice = await showConfirm(
              `⏰ Este post estava agendado para ${(post as any).scheduled_date} às ${(post as any).scheduled_time}, mas o horário já passou.\n\n` +
                `Deseja reagendar a publicação?\n\n` +
                `• SIM = Reagendar para outro horário\n` +
                `• NÃO = Publicar agora mesmo`,
              "Horário agendado já passou",
              "Reagendar",
              "Publicar agora",
            );

            if (rescheduleChoice) {
              setShowScheduleModal(postId);
              setScheduleDate(getMinDate());
              return;
            }
          }
        } catch (error) {
          console.error("Erro ao verificar data agendada:", error);
        }
      }

      const confirmPublish = await showConfirm(
        `⚠️ ATENÇÃO: Isso irá publicar REALMENTE!\n\n` +
          `Destino: ${target}\n\n` +
          "Você tem certeza que deseja publicar este post?\n\n" +
          "Esta ação não pode ser desfeita.",
        "Confirmar publicação",
        "Publicar",
        "Cancelar",
      );

      if (!confirmPublish) {
        return;
      }

      try {
        modals.setPublishingPost(postId);
        setShowPublishModal(null);

        const payload: any = {};
        if (isLinkedin) {
          payload.linkedin_type =
            linkedinType || linkedinConfig?.default_type || "personal";
        }

        const result = await apiService.post(
          API_ENDPOINTS.POSTS.REPUBLISH.replace("{id}", String(postId)),
          payload,
        );

        if (result && result.success === false) {
          const errorMsg =
            result.error || result.message || "Erro desconhecido ao publicar";
          showError(`❌ Erro na publicação: ${errorMsg}`, "Erro na publicação");
          showToastError(`Erro: ${errorMsg}`);
          return;
        }

        showSuccess(
          `🎉 POST PUBLICADO!\n\n${result.message || "Post publicado com sucesso"}\n\nDestino: ${target}\n\nID da Plataforma: ${result.platform_response || "N/A"}`,
          "Post publicado",
        );
        await loadPosts();
        showToastSuccess("Post publicado com sucesso!");
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : "Erro desconhecido";
        showError(`❌ Erro na publicação: ${errorMsg}`, "Erro na publicação");
        showToastError(`Erro: ${errorMsg}`);
      } finally {
        modals.setPublishingPost(null);
      }
    },
    [
      user,
      approvedPosts,
      linkedinConfig,
      modals,
      loadPosts,
      setShowScheduleModal,
      setScheduleDate,
      getMinDate,
    ],
  );

  const handlePublishConfirm = useCallback(
    async (publishType?: string) => {
      if (!showPublishModal) return;
      await publishPost(showPublishModal, publishType);
    },
    [showPublishModal, publishPost],
  );

  const handleMultiPlatformPublish = useCallback(
    async (platforms: string[], audioConfig?: AudioConfig) => {
      if (!modals.selectedPostForMultiPublish) return;

      let progressModal: any = null;
      let progressInterval: any = null;

      try {
        progressModal = showProgress(
          "Publicando Postagem",
          `Preparando envio para ${platforms.length} plataforma(s)...`,
        );
        let progress = 5;
        progressInterval = setInterval(() => {
          if (progress < 40) progress += 8;
          else if (progress < 70) progress += 3;
          else if (progress < 92) progress += 0.8;

          let msg = "Conectando com as redes sociais...";
          if (progress > 30) msg = "Enviando arquivos e metadados...";
          if (progress > 60) msg = "Aguardando validação da API...";
          if (progress > 85) msg = "Finalizando registros finais...";

          progressModal.update(progress, msg);
        }, 1500);

        const result = await postsService.publishMultiPlatform(
          modals.selectedPostForMultiPublish.id,
          platforms,
          audioConfig,
        );

        clearInterval(progressInterval);
        progressModal.close();

        const hasErrors =
          result.results && result.results.some((r: any) => !r.success);
        const errorsList = result.results
          ? result.results
              .filter((r: any) => !r.success)
              .map(
                (r: any) =>
                  `• ${getPlatformName(r.platform)}: ${r.message || r.error || "Erro desconhecido"}`,
              )
              .join("\n")
          : "";

        const assistantLinks: string[] = [];
        if (result.results) {
          result.results.forEach((r: any) => {
            if (r.assistant_url) assistantLinks.push(r.assistant_url);
          });
        }

        if (result.success) {
          let mainMessage =
            result.success_count > 0
              ? `Publicado com sucesso em ${result.success_count}/${result.total_count} plataformas.`
              : "Publicado com sucesso!";

          if (hasErrors) {
            showWarning(
              `${mainMessage}\n\nAlgumas falharam:\n${errorsList}`,
              "Publicação Parcial",
            );
          } else {
            showSuccess(mainMessage, "Sucesso na Publicação", {
              timer: 5000,
              timerProgressBar: true,
            });
          }

          if (assistantLinks.length > 0) {
            setTimeout(() => {
              const linksText = assistantLinks.join("\n");
              showInfo(
                `📱 Links para publicar Stories:\n\n${linksText}\n\nCopie e abra no celular!`,
                "Links para Stories",
              );
            }, 500);
          }

          modals.setShowMultiPlatformModal(null);
          modals.setSelectedPostForMultiPublish(null);
          await loadPosts();
        } else {
          // Falha total (result.success = false)
          let errorMessage = "Erro ao publicar em todas as plataformas:\n\n";
          errorMessage +=
            errorsList || "Nenhuma plataforma foi publicada com sucesso.";
          showToastError(errorMessage, 8000);
        }
      } catch (error: any) {
        if (progressInterval) clearInterval(progressInterval);
        if (progressModal) progressModal.close();
        showToastError(
          error.message || "Erro ao publicar em múltiplas plataformas",
        );
      }
    },
    [modals, loadPosts],
  );

  const handleRepublishLinkedIn = useCallback(
    async (usePersonalNetwork: boolean) => {
      if (!modals.selectedPostForRepublish) return;

      const progressModal = showProgress(
        "Republicando no LinkedIn...",
        "LinkedIn",
      );
      progressModal.update(40, "Conectando ao LinkedIn...");

      try {
        modals.setRepublishingPost(modals.selectedPostForRepublish.id);
        const result = await postsService.republishPost(
          modals.selectedPostForRepublish.id,
          usePersonalNetwork,
        );

        progressModal.close();

        if (result.success) {
          showSuccess(
            `Post republicado na ${usePersonalNetwork ? "rede pessoal" : "organização"}`,
            "Republicado",
            { timer: 5000, timerProgressBar: true },
          );
          await loadPosts();
        } else {
          showToastError("Erro na republicação do LinkedIn");
        }
      } catch (error) {
        progressModal.close();
        console.error("Erro ao republicar LinkedIn:", error);
        showToastError("Erro ao republicar no LinkedIn");
      } finally {
        modals.setRepublishingPost(null);
      }
    },
    [modals, loadPosts],
  );

  const handleRepublishMultiPlatform = useCallback(
    async (platforms: string[]) => {
      if (!modals.selectedPostForRepublish) return;

      let progressModal: any = null;
      let progressInterval: any = null;

      try {
        modals.setRepublishingPost(modals.selectedPostForRepublish.id);

        progressModal = showProgress(
          "Republicando Postagem",
          `Iniciando republicação em ${platforms.length} rede(s)...`,
        );
        let progress = 5;
        progressInterval = setInterval(() => {
          if (progress < 40) progress += 8;
          else if (progress < 70) progress += 3;
          else if (progress < 92) progress += 0.8;

          let msg = "Validando status do post...";
          if (progress > 30) msg = "Reenviando conteúdos...";
          if (progress > 65) msg = "Confirmando retorno das plataformas...";

          progressModal.update(progress, msg);
        }, 1500);

        const result = await postsService.publishMultiPlatform(
          modals.selectedPostForRepublish.id,
          platforms,
        );

        clearInterval(progressInterval);
        progressModal.close();

        const hasErrors =
          result.results && result.results.some((r: any) => !r.success);
        const errorsList = result.results
          ? result.results
              .filter((r: any) => !r.success)
              .map(
                (r: any) =>
                  `• ${getPlatformName(r.platform)}: ${r.message || r.error || "Erro desconhecido"}`,
              )
              .join("\n")
          : "";

        if (result.success) {
          let mainMessage =
            result.success_count > 0
              ? `Republicado em ${result.success_count}/${result.total_count} plataformas.`
              : "Republicado com sucesso!";

          if (hasErrors) {
            showWarning(
              `${mainMessage}\n\nAlgumas falharam:\n${errorsList}`,
              "Republicação Parcial",
            );
          } else {
            showSuccess(mainMessage, "Republicação Concluída", {
              timer: 5000,
              timerProgressBar: true,
            });
          }
          await loadPosts();
        } else {
          let errorMessage = "Erro ao republicar em todas as plataformas:\n\n";
          errorMessage +=
            errorsList || "Nenhuma plataforma foi publicada com sucesso.";
          showToastError(errorMessage, 8000);
        }
      } catch (error) {
        if (progressInterval) clearInterval(progressInterval);
        if (progressModal) progressModal.close();
        console.error("Erro ao publicar multi-plataforma:", error);
        showToastError("Erro ao publicar em múltiplas plataformas");
      } finally {
        modals.setRepublishingPost(null);
      }
    },
    [modals, loadPosts],
  );

  const handleReplyToComment = useCallback(
    async (
      platform: "instagram" | "facebook" | "linkedin",
      commentId: string,
      commentText: string,
      postUrn?: string,
    ) => {
      try {
        let url = "";
        let body: any = { comment_text: commentText };

        if (platform === "instagram") {
          url = API_ENDPOINTS.SOCIAL.INSTAGRAM_REPLY.replace("{id}", commentId);
        } else if (platform === "facebook") {
          url = API_ENDPOINTS.SOCIAL.FACEBOOK_REPLY.replace("{id}", commentId);
        } else if (platform === "linkedin") {
          url = API_ENDPOINTS.SOCIAL.LINKEDIN_REPLY.replace("{id}", commentId);
          body.post_urn = postUrn || "";
        }

        const result = await apiService.post(url, body);
        showToastSuccess("Resposta enviada com sucesso!");
        return result;
      } catch (error: any) {
        showToastError(error.message || "Erro ao responder comentário");
        throw error;
      }
    },
    [],
  );

  const openMultiPlatformModal = useCallback(
    (post: any) => {
      modals.setSelectedPostForMultiPublish(post);
      modals.setShowMultiPlatformModal(post.id);
    },
    [modals],
  );

  const openUnifiedRepublishModal = useCallback(
    (post: any) => {
      modals.setSelectedPostForRepublish(post);
      modals.setShowUnifiedRepublishModal(true);
    },
    [modals],
  );

  const postForModal = showPublishModal
    ? approvedPosts.find((p) => p.id === showPublishModal)
    : null;
  const isLinkedinModal = postForModal?.platform === "linkedin";

  return {
    showPublishModal,
    setShowPublishModal,
    linkedinConfig,
    publishOptions: isLinkedinModal ? publishOptions : undefined,
    selectedOption,
    setSelectedOption,
    showPublishOptions,
    publishPost,
    handlePublishConfirm,
    handleMultiPlatformPublish,
    handleRepublishLinkedIn,
    handleRepublishMultiPlatform,
    handleReplyToComment,
    openMultiPlatformModal,
    openUnifiedRepublishModal,
  };
}
